import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { programsApi } from '@/features/admin/api/programs.api'

const PROGRAMS_KEY = ['admin', 'programs']

export function useProgramsQuery() {
  return useQuery({ queryKey: PROGRAMS_KEY, queryFn: programsApi.list })
}

const lmsPhasesKey = (lmsSourceId: number) => ['admin', 'lms-sources', lmsSourceId, 'phases']

export function useLmsLivePhasesQuery(lmsSourceId: number, enabled = true) {
  return useQuery({
    queryKey: lmsPhasesKey(lmsSourceId),
    queryFn: () => programsApi.lmsPhases(lmsSourceId),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export function useRefreshLmsLivePhases(lmsSourceId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => programsApi.lmsPhases(lmsSourceId, true),
    onSuccess: (data) => {
      queryClient.setQueryData(lmsPhasesKey(lmsSourceId), data)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
    },
  })
}

export function useCreatePhase(lmsSourceId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { academic_phase: string; organization_id: number }) =>
      programsApi.createPhase(lmsSourceId, payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: PROGRAMS_KEY }),
  })
}

export function useDeletePhase(lmsSourceId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (academicPhase: string) => programsApi.deletePhase(lmsSourceId, academicPhase),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: PROGRAMS_KEY }),
  })
}

export function usePhaseOrganizationsQuery(lmsSourceId: number, academicPhase: string) {
  return useQuery({
    queryKey: ['admin', 'programs', lmsSourceId, 'phases', academicPhase, 'organizations'],
    queryFn: () => programsApi.getPhaseOrganizations(lmsSourceId, academicPhase),
  })
}

export function useSyncPhaseOrganizations(lmsSourceId: number, academicPhase: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (organizationIds: number[]) =>
      programsApi.syncPhaseOrganizations(lmsSourceId, academicPhase, organizationIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROGRAMS_KEY })
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'programs', lmsSourceId, 'phases', academicPhase, 'organizations'],
      })
    },
  })
}

// Organisation-level LMS mapping hooks (used in the org offcanvas drawer).
const orgMappingsKey = (orgId: number) => ['admin', 'organizations', orgId, 'lms-mappings']

export function useOrgMappingsQuery(organizationId: number) {
  return useQuery({
    queryKey: orgMappingsKey(organizationId),
    queryFn: () => programsApi.getOrgMappings(organizationId),
  })
}

export function useCreateOrgMapping(organizationId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { lms_source_id: number; academic_phase: string }) =>
      programsApi.createOrgMapping(organizationId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgMappingsKey(organizationId) })
      void queryClient.invalidateQueries({ queryKey: PROGRAMS_KEY })
    },
  })
}

export function useToggleOrgMapping(organizationId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (mappingId: number) => programsApi.toggleOrgMapping(organizationId, mappingId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: orgMappingsKey(organizationId) }),
  })
}

export function useDeleteOrgMapping(organizationId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (mappingId: number) => programsApi.deleteOrgMapping(organizationId, mappingId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgMappingsKey(organizationId) })
      void queryClient.invalidateQueries({ queryKey: PROGRAMS_KEY })
    },
  })
}
